import { apiUrl, clientHeaders, APP_BUILD } from './platform.js';

export class ApiError extends Error {
  override name = 'ApiError';
  constructor(public readonly status: number, public readonly code: string, message: string) {
    super(message);
  }
}

/** fetch() for our own API: same origin with the cookie on the website; the API's address with the app's token in the app. */
export function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  for (const [k, v] of Object.entries(clientHeaders())) headers.set(k, v);
  return fetch(apiUrl(path), { ...init, headers, credentials: APP_BUILD ? 'omit' : 'same-origin' });
}

const API_TIMEOUT_MS = 20_000;

/** JSON call to our own API. */
export async function api<T>(path: string, init: { method?: string; body?: unknown; headers?: Record<string, string> } = {}): Promise<T> {
  let res: Response;
  // A stalled connection (a lift, a basement) gives up after 20 seconds with a readable message, instead of leaving Pay on
  // "One moment…" (pre-launch audit, 25 Oct 2026). Retrying a checkout is safe: it sends the same key (lib/checkout-key.ts).
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), API_TIMEOUT_MS);
  let text: string;
  try {
    res = await apiFetch(path, {
      method: init.method ?? (init.body === undefined ? 'GET' : 'POST'),
      headers: { ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}), ...(init.headers ?? {}) },
      ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
      signal: ctrl.signal,
    });
    text = await res.text();
  } catch {
    throw new ApiError(0, 'offline', "Can't reach Tikkas. Check your connection and try again.");
  } finally {
    clearTimeout(timer);
  }
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    // Not JSON: a gateway's error page. Never handed to a screen as if it were an answer.
    if (res.ok) throw new ApiError(502, 'bad_response', 'Something went wrong. Please try again.');
    data = null;
  }
  if (!res.ok) {
    const e = (data ?? {}) as { error?: string; message?: string };
    throw new ApiError(res.status, e.error ?? 'error', e.message ?? (res.status === 401 ? 'Please sign in' : 'Something went wrong. Please try again.'));
  }
  return data as T;
}
